import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { TimelinePanel } from '../../../components/timeline/TimelinePanel'
import { ApiError } from '../../../api/problem'
import { listProfiles, listRoles, listTerritories } from '../../../api/userAdmin'
import {
  createRole,
  createTerritory,
  getCatalog,
  getObjectPermission,
  listFieldPermissions,
  listUiComponents,
  saveFieldPermission,
  saveObjectPermission,
  saveUiComponentAccess,
} from '../../../api/securityConfig'
import type { ProfileSummary, RoleView, TerritoryView } from '../../../types/userAdmin'
import type {
  FieldPermissionView,
  ObjectPermissionView,
  SecurityConfigCatalog,
  UiComponentAccessView,
} from '../../../types/securityConfig'

type Tab = 'ols' | 'fls' | 'ui' | 'roles' | 'territories' | 'history'

const TABS: { key: Tab; label: string }[] = [
  { key: 'ols', label: 'Object-level security' },
  { key: 'fls', label: 'Field-level security' },
  { key: 'ui', label: 'UI components' },
  { key: 'roles', label: 'Role tree' },
  { key: 'territories', label: 'Territory tree' },
  { key: 'history', label: 'History' },
]

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * `/admin/security` — IB-337 (≡ IB-88 ≡ IB-43, `docs/design/role-model-reconciliation.md` §4): the
 * firm-admin Security Administration console (FR-M4.1–M4.5). Every read/write here is gated
 * server-side on `security_config:F` (`CapabilitySecurityConfigGuard`) — a denied caller sees a
 * real 403 `ApiError`, never a silently-hidden button.
 *
 * <p><b>Guarded-field invariant (AC4, CLAUDE.md §2.1).</b> The FLS tab NEVER lets a save on a
 * guarded field imply a write grant — `guardedField`/`note` come straight from the backend, and the
 * UI renders the note as an inline warning instead of a plain "saved" toast whenever it is set.
 *
 * <p><b>Honest scope note.</b> No dedicated "effective access preview" (layering across Profile ∪
 * multiple PermissionSets) — the underlying `object_permission`/`field_permission`/
 * `ui_component_access` tables are Profile-keyed only (V19), so this console edits per-Profile
 * config; a PermissionSet-layered preview is a disclosed follow-up, not silently implied here.
 * "Rollback" resubmits the exact prior content read off the History tab (tagged
 * `method=rollback` server-side) rather than a dedicated one-click button against a stored
 * version id — see `SecurityConfigAdminService`'s own javadoc for why.
 */
export function SecurityAdministrationScreen() {
  const [tab, setTab] = useState<Tab>('ols')

  const [profiles, setProfiles] = useState<ProfileSummary[]>([])
  const [profileId, setProfileId] = useState('')
  const [catalog, setCatalog] = useState<SecurityConfigCatalog | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([listProfiles(controller.signal), getCatalog(controller.signal)])
      .then(([p, c]) => {
        setProfiles(p)
        setCatalog(c)
        const first = p[0]
        if (first) setProfileId((prev) => prev || first.id)
      })
      .catch((err) => setLoadError(errorMessage(err, 'Could not load the Profile/catalog reference data.')))
    return () => controller.abort()
  }, [])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="shield" size={22} />
        </span>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>Security Administration</h1>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Object/Field-level security, UI-component visibility, and Role/Territory trees for this firm.
          </p>
        </div>
      </div>

      {loadError ? <InlineAlert severity="error">{loadError}</InlineAlert> : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <label htmlFor="security-admin-profile" style={{ fontSize: 'var(--iw-size-sm)' }}>
          Profile
        </label>
        <select
          id="security-admin-profile"
          className="iw-select"
          value={profileId}
          onChange={(e) => setProfileId(e.target.value)}
          style={{ minWidth: 260 }}
        >
          <option value="" disabled>
            Select…
          </option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} {p.canon ? '(canon)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div role="tablist" aria-label="Security Administration sections" style={{ display: 'flex', gap: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className="iw-btn iw-btn--ghost"
            style={tab === t.key ? { borderBottom: '2px solid var(--iw-accent)', borderRadius: 0, color: 'var(--iw-accent)' } : { borderRadius: 0 }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!profileId ? (
        <InlineAlert severity="info">Select a Profile above to view or edit its security configuration.</InlineAlert>
      ) : (
        <>
          {tab === 'ols' ? <ObjectPermissionsPanel profileId={profileId} catalog={catalog} /> : null}
          {tab === 'fls' ? <FieldPermissionsPanel profileId={profileId} catalog={catalog} /> : null}
          {tab === 'ui' ? <UiComponentsPanel profileId={profileId} catalog={catalog} /> : null}
          {tab === 'roles' ? <RoleTreePanel /> : null}
          {tab === 'territories' ? <TerritoryTreePanel /> : null}
          {tab === 'history' ? (
            <div role="tabpanel" aria-label="History">
              <TimelinePanel target={{ type: 'SecurityConfig', id: profileId }} />
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

function ObjectPermissionsPanel({ profileId, catalog }: { profileId: string; catalog: SecurityConfigCatalog | null }) {
  const [objectType, setObjectType] = useState('')
  const [current, setCurrent] = useState<ObjectPermissionView | null>(null)
  const [viewAll, setViewAll] = useState(false)
  const [modifyAll, setModifyAll] = useState(false)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (catalog && catalog.objectTypes[0] && !objectType) setObjectType(catalog.objectTypes[0])
  }, [catalog, objectType])

  const load = useCallback(() => {
    if (!profileId || !objectType) return
    getObjectPermission(profileId, objectType)
      .then((view) => {
        setCurrent(view)
        setViewAll(view.viewAll)
        setModifyAll(view.modifyAll)
      })
      .catch((err) => setError(errorMessage(err, 'Could not load the current OLS setting.')))
  }, [profileId, objectType])

  useEffect(() => {
    setError(null)
    setNotice(null)
    load()
  }, [load])

  async function handleSave() {
    if (saving) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const saved = await saveObjectPermission({ profileId, objectType, viewAll, modifyAll, reason })
      setCurrent(saved)
      setReason('')
      setNotice(saved.changed ? 'Saved.' : 'No change to save.')
    } catch (err) {
      setError(errorMessage(err, 'Could not save this OLS setting.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div role="tabpanel" aria-label="Object-level security" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 640 }}>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'center' }}>
        <label htmlFor="ols-object-type" style={{ fontSize: 'var(--iw-size-sm)' }}>
          Object type
        </label>
        <select id="ols-object-type" className="iw-select" value={objectType} onChange={(e) => setObjectType(e.target.value)} style={{ flex: 1 }}>
          {(catalog?.objectTypes ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 'var(--iw-space-4)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
          <input type="checkbox" checked={viewAll} onChange={(e) => setViewAll(e.target.checked)} />
          View all rows regardless of ownership
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
          <input type="checkbox" checked={modifyAll} onChange={(e) => setModifyAll(e.target.checked)} />
          Modify all rows regardless of ownership
        </label>
      </div>

      {current ? (
        <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          {current.tenantOverride ? 'A tenant-specific override is set for this Profile/object.' : 'Showing the canon default — no tenant-specific override yet.'}
        </p>
      ) : null}

      <label htmlFor="ols-reason" style={{ fontSize: 'var(--iw-size-sm)' }}>
        Change reason (required)
      </label>
      <textarea
        id="ols-reason"
        className="iw-input"
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this change being made?"
      />

      <div>
        <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSave()} disabled={saving || reason.trim().length < 10}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function FieldPermissionsPanel({ profileId, catalog }: { profileId: string; catalog: SecurityConfigCatalog | null }) {
  const [objectType, setObjectType] = useState('')
  const [fields, setFields] = useState<FieldPermissionView[]>([])
  const [levelByField, setLevelByField] = useState<Record<string, 'hidden' | 'read' | 'edit'>>({})
  const [reason, setReason] = useState('')
  const [savingField, setSavingField] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (catalog && catalog.objectTypes[0] && !objectType) setObjectType(catalog.objectTypes[0])
  }, [catalog, objectType])

  const load = useCallback(() => {
    if (!profileId || !objectType) return
    listFieldPermissions(profileId, objectType)
      .then((rows) => {
        setFields(rows)
        setLevelByField(Object.fromEntries(rows.map((r) => [r.fieldName, r.level ?? 'edit'])))
      })
      .catch((err) => setError(errorMessage(err, 'Could not load the field catalog for this object.')))
  }, [profileId, objectType])

  useEffect(() => {
    setError(null)
    setNotice(null)
    load()
  }, [load])

  async function handleSave(fieldName: string) {
    if (savingField || reason.trim().length < 10) return
    setSavingField(fieldName)
    setError(null)
    setNotice(null)
    try {
      const level = levelByField[fieldName] ?? 'edit'
      const saved = await saveFieldPermission({ profileId, objectType, fieldName, level, reason })
      setFields((prev) => prev.map((f) => (f.fieldName === fieldName ? saved : f)))
      setNotice(saved.guardedField ? (saved.note ?? 'Saved.') : 'Saved.')
    } catch (err) {
      setError(errorMessage(err, 'Could not save this field-level security setting.'))
    } finally {
      setSavingField(null)
    }
  }

  return (
    <div role="tabpanel" aria-label="Field-level security" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 760 }}>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'center' }}>
        <label htmlFor="fls-object-type" style={{ fontSize: 'var(--iw-size-sm)' }}>
          Object type
        </label>
        <select id="fls-object-type" className="iw-select" value={objectType} onChange={(e) => setObjectType(e.target.value)} style={{ flex: 1 }}>
          {(catalog?.objectTypes ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <label htmlFor="fls-reason" style={{ fontSize: 'var(--iw-size-sm)' }}>
        Change reason (required, applies to whichever field you Save below)
      </label>
      <textarea id="fls-reason" className="iw-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />

      <table className="iw-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Field</th>
            <th style={{ textAlign: 'left' }}>Access</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.fieldName}>
              <td>
                {f.fieldName}
                {f.guardedField ? (
                  <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                    Guarded field — {f.note ?? 'edit visibility only, writes still route through the adjustment path'}
                  </div>
                ) : null}
              </td>
              <td>
                <select
                  className="iw-select"
                  aria-label={`Access for ${f.fieldName}`}
                  value={levelByField[f.fieldName] ?? 'edit'}
                  onChange={(e) => setLevelByField((prev) => ({ ...prev, [f.fieldName]: e.target.value as 'hidden' | 'read' | 'edit' }))}
                >
                  <option value="hidden">Hidden</option>
                  <option value="read">Read only</option>
                  <option value="edit">Edit{f.guardedField ? ' (visibility only)' : ''}</option>
                </select>
              </td>
              <td>
                <button
                  type="button"
                  className="iw-btn iw-btn--sm"
                  onClick={() => void handleSave(f.fieldName)}
                  disabled={savingField === f.fieldName || reason.trim().length < 10}
                >
                  {savingField === f.fieldName ? 'Saving…' : 'Save'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function UiComponentsPanel({ profileId, catalog }: { profileId: string; catalog: SecurityConfigCatalog | null }) {
  const [components, setComponents] = useState<UiComponentAccessView[]>([])
  const [reason, setReason] = useState('')
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!profileId) return
    listUiComponents(profileId)
      .then(setComponents)
      .catch((err) => setError(errorMessage(err, 'Could not load UI-component visibility.')))
  }, [profileId])

  useEffect(() => {
    setError(null)
    setNotice(null)
    load()
  }, [load])

  async function handleToggle(componentKey: string, nextVisible: boolean) {
    if (savingKey || reason.trim().length < 10) return
    setSavingKey(componentKey)
    setError(null)
    setNotice(null)
    try {
      const saved = await saveUiComponentAccess({ profileId, componentKey, visible: nextVisible, reason })
      setComponents((prev) => prev.map((c) => (c.componentKey === componentKey ? saved : c)))
      setNotice('Saved.')
    } catch (err) {
      setError(errorMessage(err, 'Could not save this UI-component setting.'))
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div role="tabpanel" aria-label="UI components" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 640 }}>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}

      <label htmlFor="ui-reason" style={{ fontSize: 'var(--iw-size-sm)' }}>
        Change reason (required, applies to whichever component you toggle below)
      </label>
      <textarea id="ui-reason" className="iw-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
        {(catalog?.componentKeys ?? []).map((key) => {
          const row = components.find((c) => c.componentKey === key)
          const visible = row?.visible ?? false
          return (
            <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
              <span style={{ flex: 1 }}>{key}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => void handleToggle(key, e.target.checked)}
                  disabled={savingKey === key || reason.trim().length < 10}
                />
                Visible
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function RoleTreePanel() {
  const [roles, setRoles] = useState<RoleView[]>([])
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(() => {
    listRoles()
      .then(setRoles)
      .catch((err) => setError(errorMessage(err, 'Could not load the Role tree.')))
  }, [])

  useEffect(load, [load])

  async function handleCreate() {
    if (!key.trim() || !name.trim() || saving) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await createRole(key.trim(), name.trim(), parentId || null)
      setKey('')
      setName('')
      setParentId('')
      setNotice(`Created Role "${name}".`)
      load()
    } catch (err) {
      setError(errorMessage(err, 'Could not create that Role.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div role="tabpanel" aria-label="Role tree" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 640 }}>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
        {roles.map((r) => (
          <li key={r.id}>{r.parentId ? `— ${r.name}` : r.name}</li>
        ))}
      </ul>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', borderTop: '1px solid var(--iw-border)', paddingTop: 'var(--iw-space-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--iw-size-sm)' }}>Add a Role node</h3>
        <input className="iw-input" aria-label="Role key" placeholder="senior_consultant" value={key} onChange={(e) => setKey(e.target.value)} />
        <input className="iw-input" aria-label="Role name" placeholder="Senior Consultant" value={name} onChange={(e) => setName(e.target.value)} />
        <select className="iw-select" aria-label="Parent Role" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">(root)</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <div>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleCreate()} disabled={saving || !key.trim() || !name.trim()}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

function TerritoryTreePanel() {
  const [territories, setTerritories] = useState<TerritoryView[]>([])
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'geo' | 'named'>('geo')
  const [parentId, setParentId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(() => {
    listTerritories()
      .then(setTerritories)
      .catch((err) => setError(errorMessage(err, 'Could not load the Territory tree.')))
  }, [])

  useEffect(load, [load])

  async function handleCreate() {
    if (!key.trim() || !name.trim() || saving) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await createTerritory(key.trim(), name.trim(), kind, parentId || null)
      setKey('')
      setName('')
      setParentId('')
      setNotice(`Created Territory "${name}".`)
      load()
    } catch (err) {
      setError(errorMessage(err, 'Could not create that Territory.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div role="tabpanel" aria-label="Territory tree" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 640 }}>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
        {territories.map((t) => (
          <li key={t.id}>
            {t.parentId ? `— ${t.name}` : t.name} <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>({t.kind})</span>
          </li>
        ))}
      </ul>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', borderTop: '1px solid var(--iw-border)', paddingTop: 'var(--iw-space-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--iw-size-sm)' }}>Add a Territory node</h3>
        <input className="iw-input" aria-label="Territory key" placeholder="east_region" value={key} onChange={(e) => setKey(e.target.value)} />
        <input className="iw-input" aria-label="Territory name" placeholder="East Region" value={name} onChange={(e) => setName(e.target.value)} />
        <select className="iw-select" aria-label="Territory kind" value={kind} onChange={(e) => setKind(e.target.value as 'geo' | 'named')}>
          <option value="geo">Geo</option>
          <option value="named">Named</option>
        </select>
        <select className="iw-select" aria-label="Parent Territory" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">(root)</option>
          {territories.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <div>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleCreate()} disabled={saving || !key.trim() || !name.trim()}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}
